import type { IResponse } from '@/features/api/extranet/dto/types'
import {
  HabilitationAddErreurDoublon,
  type EntiteDTO,
  type HabilitationAddDTO,
  type HabilitationDTO,
  type HabilitationDemandeDTO,
} from '@/features/api/habilitation/dto'
import type { ContactDTO } from '@/features/api/habilitation/dto/contact'
import type { ContextAppType } from '@/features/auth/context-app/types'
import { buildFetch } from '@/features/fetch/buildFetch'
import { habilitations } from '@/features/fetch/flux/habilitations'
import { ensureSuccessStatusCode } from '@/features/fetch/ensureSuccessStatusCode'
import type {
  HabilitationAdminType,
  HabilitationsAdminMode,
} from '@/features/interface/profil-utilisateur/sections/admin/HabilitationsAdmin.type'

export function buildRattachementAdminService(options: {
  baseUrl: string
  token: string
  context: ContextAppType
  type: HabilitationAdminType
  mode?: HabilitationsAdminMode
}) {
  const fetch = buildFetch(habilitations)

  const INIT_HEADERS: RequestInit = {
    headers: new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${options.token}`,
    }),
  }

  async function searchContact(search: string, init?: RequestInit) {
    init = { ...INIT_HEADERS, ...init, dialogMode: 'silent' }
    const response = await fetch(
      `${options.baseUrl}/contacts/${options.context}?search=${encodeURIComponent(search)}`,
      init,
    )
    ensureSuccessStatusCode(response)

    const result = (await response.json()) as IResponse<ContactDTO[]>
    return result.data
  }

  async function getContact(id: string, init?: RequestInit): Promise<ContactDTO | null> {
    init = { ...INIT_HEADERS, ...init, dialogMode: 'silent' }
    const url = `${options.baseUrl}/contacts/${options.context}/${encodeURIComponent(id)}`
    const response = await fetch(url, init)
    if (response.status === 404) return null
    ensureSuccessStatusCode(response)

    const result = (await response.json()) as IResponse<ContactDTO>
    return result.data
  }

  async function searchEntite(search: string, init?: RequestInit) {
    init = { ...INIT_HEADERS, ...init }
    const response = await fetch(`${options.baseUrl}/entites/${options.context}?search=${search}`, init)
    ensureSuccessStatusCode(response)

    const result = (await response.json()) as IResponse<EntiteDTO[]>
    return result.data
  }

  async function getEntite(id: string, init?: RequestInit): Promise<EntiteDTO | null> {
    init = { ...INIT_HEADERS, ...init, dialogMode: 'silent' }
    const response = await fetch(`${options.baseUrl}/entites/${options.context}/${id}`, init)
    if (response.status === 404) return null
    ensureSuccessStatusCode(response)

    const result = (await response.json()) as IResponse<EntiteDTO>
    return result.data
  }

  // Todo : Retirer patchPRPASupprimer pour la mep de juin 2026
  function patchPRPASupprimer(habilitation: HabilitationDTO) {
    const permissions = habilitation.autorisations.permissions.filter(p => p.type !== 'PRP' as any)
    return {
      ...habilitation,
      autorisations: {
        ...habilitation.autorisations,
        permissions
      }
    } as HabilitationDTO
  }

  async function getHabilitations(id?: string | null, init?: RequestInit): Promise<HabilitationDTO[] | null> {
    init = { sablier: true, ...INIT_HEADERS, ...init, dialogExcludeErrors: [404] }

    const fetchResult = (() => {
      if (options.type === 'utilisateur')
        return fetch(`${options.baseUrl}/habilitations/${options.context}`, init)
      else if (options.mode === 'habilitations')
        return fetch(`${options.baseUrl}/habilitations/${options.context}/pending/admin`, init)
      else
        return fetch(`${options.baseUrl}/habilitations/${options.context}/${options.mode}/${id}`, init)
    })()

    const response = await fetchResult
    if (response.status === 404) return null
    ensureSuccessStatusCode(response)

    const result = (await response.json()) as IResponse<HabilitationDTO[]>

    // Todo : Retirer patchPRPASupprimer et revenir à return result.data pour la mep de juin 2026
    // return result.data
    return result.data.map(patchPRPASupprimer)
  }

  async function getDemandesEnAttente(init?: RequestInit): Promise<HabilitationDemandeDTO[] | null> {
    if (options.type === 'administrateur') throw 'Opération inattendue'
    init = { sablier: true, ...INIT_HEADERS, ...init, dialogExcludeErrors: [404] }

    const response = await fetch(`${options.baseUrl}/habilitations/${options.context}/pending`, init)
    if (response.status === 404) return null
    ensureSuccessStatusCode(response)

    const result = (await response.json()) as IResponse<HabilitationDemandeDTO[]>
    return result.data
  }

  async function updateHabilitationPermissions(
    habilitation: HabilitationDTO,
    request?: RequestInit,
  ): Promise<HabilitationDTO | null> {
    request = {
      sablier: true,
      ...INIT_HEADERS,
      ...request,
      method: 'PUT',
      body: JSON.stringify({
        id: habilitation.id,
        autorisations: habilitation.autorisations,
      }),
    }
    const response = await fetch(`${options.baseUrl}/habilitations/${options.context}/${habilitation.id}`, request)

    if (response.status === 404) return null
    ensureSuccessStatusCode(response)

    const result = (await response.json()) as IResponse<HabilitationDTO>
    // Todo : Retirer patchPRPASupprimer et revenir à return result.data pour la mep de juin 2026
    return patchPRPASupprimer(result.data)
  }

  async function addHabilitation(habilitationAdd: HabilitationAddDTO, request?: RequestInit): Promise<HabilitationDTO> {
    request = {
      sablier: true,
      dialogMode: 'retry',
      dialogExcludeErrors: [400], // Existe déja
      ...INIT_HEADERS,
      ...request,
      method: 'POST',
      body: JSON.stringify(habilitationAdd),
    }

    // Todo : ajouter-drf la gestion de la concurrence
    const response = await fetch(`${options.baseUrl}/habilitations/${options.context}`, request)

    if (response.status === 400) throw new HabilitationAddErreurDoublon()
    ensureSuccessStatusCode(response)

    const result = (await response.json()) as IResponse<HabilitationDTO>
    // Todo : Retirer patchPRPASupprimer et revenir à return result.data pour la mep de juin 2026
    return patchPRPASupprimer(result.data)
  }

  async function deleteHabilitation(habilitation: HabilitationDTO, request?: RequestInit): Promise<void> {
    request = {
      sablier: true,
      dialogMode: 'retry',
      dialogExcludeErrors: [400],
      ...INIT_HEADERS,
      ...request,
      method: 'DELETE',
    }

    // Todo : ajouter-drf la gestion de la concurrence
    const response = await fetch(`${options.baseUrl}/habilitations/${options.context}/${habilitation.id}`, request)

    // L'habilitation n'existe pas
    if (response.status === 400) return

    ensureSuccessStatusCode(response)
  }

  return {
    addHabilitation,
    deleteHabilitation,
    getContact,
    getEntite,
    getHabilitations,
    getDemandesEnAttente,
    searchContact,
    searchEntite,
    updateHabilitationPermissions,
  }
}
